import React, { useState } from 'react';
import { Button } from './ui/button';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from './ui/sheet';
import { Menu, Globe } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const Navbar: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [logoError, setLogoError] = useState(false);
  const { language, setLanguage, t } = useLanguage();

  const handleLogoError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    console.error('Logo image failed to load:', {
      src: (e.target as HTMLImageElement).src,
      error: 'Image load error or URL returned non-image content'
    });
    setLogoError(true);
  };

  const navItems = [
    { label: t('navServices'), href: '#services' },
    { label: t('navAbout'), href: '#about' },
    { label: t('navTestimonials'), href: '#testimonials' },
  ];

  const toggleLanguage = () => {
    setLanguage(language === 'zh' ? 'en' : 'zh');
  };

  return (
    <nav className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="container flex h-16 items-center justify-between px-4">
        {/* Logo */}
        <div className="flex items-center space-x-2">
          {logoError ? (
            <div className="text-lg font-bold text-primary">
              家家顺搬家
            </div>
          ) : (
            <img 
              src="/images/MR-Logo.png" 
              alt="家家顺搬家公司" 
              className="h-10 w-auto"
              onError={handleLogoError}
              onLoad={() => console.log('Logo image loaded successfully')}
              loading="eager"
            />
          )}
        </div>
        
        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center space-x-6">
          {navItems.map((item) => (
            <a
              key={item.href}
              href={item.href}
              className="text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
            >
              {item.label}
            </a>
          ))}
          <Button
            variant="ghost"
            onClick={toggleLanguage}
            className="h-10 px-3 text-muted-foreground hover:text-foreground flex items-center gap-2"
            aria-label={language === 'zh' ? 'Switch to English' : '切换到中文'}
          >
            <Globe className="h-5 w-5" />
            <span className="text-sm font-medium">{language === 'zh' ? 'EN' : '中文'}</span>
          </Button>
          <Button asChild className="bg-primary hover:bg-primary/90">
            <a href="#contact">{t('navContact')}</a>
          </Button>
        </div>

        {/* Mobile Menu */}
        <Sheet open={isMenuOpen} onOpenChange={setIsMenuOpen}>
          <SheetTrigger asChild className="md:hidden">
            <Button variant="ghost" size="icon">
              <Menu className="h-6 w-6" />
              <span className="sr-only">{t('navOpenMenu')}</span>
            </Button>
          </SheetTrigger>
          <SheetContent side="right" className="w-[300px] sm:w-[400px]">
            <SheetHeader>
              <SheetTitle>{t('navMenu')}</SheetTitle>
              <SheetDescription>
                {t('navMenuDesc')}
              </SheetDescription>
            </SheetHeader>
            <div className="mt-8 flex flex-col space-y-4">
              {navItems.map((item) => (
                <a
                  key={item.href}
                  href={item.href}
                  onClick={() => setIsMenuOpen(false)}
                  className="text-lg font-medium text-foreground transition-colors hover:text-primary"
                >
                  {item.label}
                </a>
              ))}
              <Button
                variant="ghost"
                onClick={() => {
                  toggleLanguage();
                }}
                className="w-full justify-start"
              >
                <Globe className="h-5 w-5 mr-2" />
                {language === 'zh' ? 'Switch to English' : '切换到中文'}
              </Button>
              <Button asChild className="mt-4 w-full">
                <a href="#contact" onClick={() => setIsMenuOpen(false)}>
                  {t('navContact')}
                </a>
              </Button>
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </nav>
  );
};

export default Navbar;
